import { useEffect, useRef, useState, type ReactNode } from "react";
import { Icon } from "../icons";

const DURATION_MS = 1400;

/**
 * Bewusste Geste für riskante Aktionen (docs/16 §2): Maus, Touch oder Leertaste/Enter halten.
 * Die Geste ist reine Bedienführung; wirksam ist allein die serverseitige Freigabe.
 */
export function HoldButton({
  onConfirm,
  children,
  disabled,
  describedBy,
  onTooShort,
}: {
  onConfirm: () => void;
  children: ReactNode;
  disabled?: boolean;
  describedBy?: string;
  onTooShort?: () => void;
}) {
  const [progress, setProgress] = useState(0);
  const timer = useRef<number | null>(null);
  const start = useRef(0);
  const done = useRef(false);
  const progressRef = useRef(0);

  const stop = (completed: boolean) => {
    if (timer.current !== null) window.clearInterval(timer.current);
    timer.current = null;
    if (!completed && !done.current) {
      if (progressRef.current > 0 && progressRef.current < 1) onTooShort?.();
      progressRef.current = 0;
      setProgress(0);
    }
  };

  const begin = () => {
    if (disabled || timer.current !== null || done.current) return;
    start.current = performance.now();
    timer.current = window.setInterval(() => {
      const p = Math.min(1, (performance.now() - start.current) / DURATION_MS);
      progressRef.current = p;
      setProgress(p);
      if (p >= 1) {
        done.current = true;
        stop(true);
        onConfirm();
      }
    }, 16);
  };

  useEffect(
    () => () => {
      if (timer.current !== null) window.clearInterval(timer.current);
    },
    [],
  );

  return (
    <button
      type="button"
      className="btn btn--primary btn--hold"
      disabled={disabled}
      aria-describedby={describedBy}
      aria-label={typeof children === "string" ? `${children}: zum Bestätigen gedrückt halten` : undefined}
      onPointerDown={(e) => {
        e.preventDefault();
        // Den Finger festhalten: kleines Verrutschen beim Halten bricht sonst ab.
        e.currentTarget.setPointerCapture?.(e.pointerId);
        begin();
      }}
      onPointerUp={() => stop(false)}
      onPointerLeave={() => stop(false)}
      onPointerCancel={() => stop(false)}
      // Langer Druck auf dem Handy öffnet sonst Menü oder Textauswahl und bricht das Halten nach etwa 0,5 s ab.
      onContextMenu={(e) => e.preventDefault()}
      onKeyDown={(e) => {
        if (e.key === " " || e.key === "Enter") {
          e.preventDefault();
          if (!e.repeat) begin();
        }
      }}
      onKeyUp={(e) => {
        if (e.key === " " || e.key === "Enter") stop(false);
      }}
      onClick={(e) => e.preventDefault()}
    >
      <span className="btn-fill" style={{ ["--p" as string]: progress.toFixed(3) }} />
      <span className="btn-label">
        {children} <Icon name="arrow" />
      </span>
    </button>
  );
}
